/**
 * The plan's pattern week: seven weekdays, or one tile per day of a self-paced cycle.
 *
 * Promoted out of `pages/plan-library` (it was `WeekTiles`) on 23 September 2026, when „Nowy
 * program"'s template preview became its second consumer. Both hosts wear it unchanged; where it
 * stands inside each of them is the host's `className`, and nothing the stylesheet below declares.
 *
 * ── The section holds `--h-pl-week` from the first frame ─────────────────────────────────────
 * In the library's inspector it is the first thing under the hero, so anything it does to its own
 * height moves the muscle split and the assigned strip below it. The reserve is on the SECTION and the heading, the grid
 * and the tiles all stand inside it whether the read has answered or not; the gate hides the bars.
 *
 * A self-paced plan with more or fewer than seven days still fills that reserve: the tiles share
 * the row, so ten days are ten narrower tiles rather than a second line. The cap is the builder's
 * own — a cycle runs to seven days on the weekday axis and the tape is not drawn here.
 *
 * ── The captions are the app's OWN weekday words, reused ─────────────────────────────────────
 * `web:trainingWeek.dow` carries exactly the canon's `Pn Wt Śr Cz Pt So Nd`, for exactly this
 * purpose — a compact weekday header — and `shared/ui/TrainingWeekStrip` already draws them.
 * `web:weekdays.short` is the other set and spells them `Pon / Czw / Sob`, which is the calendar's
 * canon and not this one. The one thing to keep straight is the base: that key set is numbered from
 * ZERO for Monday and `dayOfWeek` on the wire is numbered from one, so the conversion happens here,
 * once, with this sentence beside it.
 *
 * ── `wolne` is a DIFFERENT statement from a day with nothing in it ───────────────────────────
 * A tile with no day is a slot the plan deliberately leaves free and reads `wolne`, unboxed and in
 * `--text-3`. A day the trainer created and has not filled yet is present, named, and reads
 * `0 ćw.` — the projection keeps the two apart and so does this.
 */

import { useTranslation } from 'react-i18next';

import paneHeading from '~/shared/ui/paneHeading.module.css';
import skel from '~/shared/ui/skeleton.module.css';

import type { PatternWeekView, TileCaption } from '../model/patternWeek';
import styles from './PatternWeek.module.css';

interface PatternWeekProps {
  /** `null` while `/full` is still open. The section stands either way. */
  readonly view: PatternWeekView | null;
  readonly skeleton: boolean;
  /** `useSkeletonGate`'s hand-over class for the tiles the answer brings — `''` before then. */
  readonly reveal: string;
  /** Where the section stands inside its host — its inset. Layout only; see the header. */
  readonly className?: string;
}

/** How many tiles the reserve is drawn with before the plan says otherwise. */
const PLACEHOLDER_TILES = [0, 1, 2, 3, 4, 5, 6];

export function PatternWeek({ view, skeleton, reveal, className }: PatternWeekProps) {
  const { t } = useTranslation();

  const caption = (tile: TileCaption): string =>
    tile.kind === 'weekday'
      ? // `dayOfWeek` counts Monday as 1; `web:trainingWeek.dow` counts it as 0.
        t(`web:trainingWeek.dow.${String(tile.weekday - 1)}`)
      : t('web:planLibrary.inspector.week.dayOrdinal', { count: tile.position });

  /* What a day the trainer never named is called — see the header. `web:weekdays.full` is keyed
   * from ONE for Monday, which is `dayOfWeek`'s own numbering, so this one takes the wire's value
   * unshifted while the caption above it still has to subtract. Two key sets, two bases, and the
   * difference is why both conversions are written out rather than shared. */
  const unnamed = (tile: TileCaption): string =>
    tile.kind === 'weekday'
      ? t(`web:weekdays.full.${String(tile.weekday)}`)
      : t('web:planLibrary.inspector.week.dayFull', { count: tile.position });

  return (
    <section className={`${String(styles.section)} ${className ?? ''}`}>
      {/* A real heading: the pane has sections and a reader user could not jump between them. */}
      <h3 className={paneHeading.heading}>
        {view === null || view.weekLabel.kind === 'week'
          ? t('web:planLibrary.inspector.week.eyebrow')
          : t('web:planLibrary.inspector.week.cycleEyebrow', { count: view.weekLabel.days })}
      </h3>

      <div className={styles.grid}>
        {view === null
          ? // The SAME two boxes the answer draws, at the same heights, holding bars instead of
            // words. The tile wears the neutral filled skin rather than the unboxed one: a wait must
            // not claim the day is free before the plan has said so.
            PLACEHOLDER_TILES.map((index) => (
              <div key={index} className={styles.column}>
                <span className={styles.dow}>
                  {skeleton ? (
                    <i className={`${String(skel.bar)} ${String(styles.skelDay)}`} />
                  ) : null}
                </span>
                <div className={styles.tile}>
                  {skeleton ? (
                    <>
                      <i className={`${String(skel.bar)} ${String(styles.skelName)}`} />
                      <i className={`${String(skel.bar)} ${String(styles.skelCount)}`} />
                    </>
                  ) : null}
                </div>
              </div>
            ))
          : view.weekTiles.map((tile, index) => (
              <div key={index} className={`${String(styles.column)} ${reveal}`}>
                <span className={`${String(styles.dow)} ${tile.day === null ? String(styles.dowFree) : ''}`}>
                  {caption(tile.caption)}
                </span>
                <div
                  className={`${String(styles.tile)} ${String(tile.day === null ? styles.tileFree : styles.tileDay)}`}
                >
                  {/* The plan's OWN name for this day — and the WEEKDAY only when the trainer never
                    * gave it one, because then the day really is named after the day. The caption
                    * above already says which slot this is. */}
                  <span className={`${String(styles.name)} ${tile.day === null ? String(styles.free) : ''}`}>
                    {tile.day === null
                      ? t('web:planLibrary.inspector.week.free')
                      : tile.day.name || unnamed(tile.caption)}
                  </span>
                  {/* The specimen keeps this span on a free tile too, EMPTY, and that is what sits
                    * `wolne` where it sits: the tile stacks from its foot, so a zero-height second
                    * line lifts the first by its own leading. Dropping it moves the word 11px. */}
                  <span className={styles.count}>
                    {tile.day === null
                      ? ''
                      : t('web:planLibrary.inspector.week.exercises', {
                          count: tile.day.exerciseCount,
                        })}
                  </span>
                </div>
              </div>
            ))}
      </div>
    </section>
  );
}
